import { Button, Chip, makeStyles, Typography } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import StarIcon from "@material-ui/icons/Star";
import Container from "@saleor/components/Container";
import useNavigator from "@saleor/hooks/useNavigator";
import {
  FilterPageProps,
  PageListProps,
  SortPage,
  TabPageProps
} from "@saleor/types";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import couponRedemptionIcon from "../../../../assets/images/coupon-icon.svg";
import { CouponRedemptions_couponRedemptions_edges_node } from "../../types/CouponRedemptions";
import {
  couponListUrl,
  couponManageUrl,
  CouponRedemptionListUrlSortField,
  CouponsUrlQueryParams
} from "../../url";
import FilterBar from "../CouponFilterBar";
import { fiberIconColors, statusColors } from "../CouponRankingList/style";
import CouponRedemptionList from "../CouponRedemptionList";
import {
  CouponRedemptionFilterKeys,
  CouponRedemptionListUrlFilterOpts,
  createFilterStructure
} from "./filters";

const useStyles = makeStyles(
  theme => ({
    auxilliaryContainers: {
      alignItems: "center",
      display: "flex",
      justifyContent: "flex-end"
    },
    backBtn: {
      "&:hover": {
        color: "#0c76f7"
      },
      alignItems: "center",
      color: "#000000",
      cursor: "pointer",
      display: "flex",
      marginBottom: "15px",
      marginTop: "30px",
      textDecoration: "none"
    },
    buttonContainers: {
      alignItems: "center",
      display: "flex",
      justifyContent: "space-between"
    },
    buttonNavigation: {
      marginBottom: "100px"
    },
    card: {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      height: "100%",
      justifyContent: "center"
    },
    chips: {
      marginBottom: "15px",
      marginLeft: theme.spacing(1),
      marginTop: "32px"
    },
    couponAvailable: {
      color: "#747C8C",
      display: "flex",
      flexDirection: "column",
      fontSize: "16px",
      justifyContent: "center",
      marginBottom: "25px"
    },
    couponIcon: {
      height: "95px",
      width: "95px"
    },
    exportBtnText: {
      color: theme.palette.primary.main,
      fontSize: 14,
      fontWeight: 600 as 600,
      textTransform: "uppercase"
    },
    exportButton: {
      border: "2px solid #1848FE"
    },
    exportButtonContainer: {
      alignItems: "center",
      display: "flex",
      marginTop: "20px"
    },
    featuredStarIcon: {
      color: "#FDD835",
      marginBottom: "15px",
      marginLeft: theme.spacing(1),
      marginTop: "32px"
    },
    imageContainer: {
      margin: "100px 0 10px 0",
      padding: "10px"
    },
    label: {
      cursor: "pointer",
      fontSize: "20px !important",
      fontWeight: 600,
      marginBottom: "15px",
      marginLeft: "10px",
      marginTop: "30px"
    },
    noCoupons: {
      height: "448px",
      width: "1255px"
    },
    noCouponsDesc: {
      fontSize: "20px",
      margin: "10px 0 30px 0"
    },
    noCouponsText: {
      fontSize: "29.9px !important",
      fontWeight: 600
    },
    refreshIcon: {
      color: "#247CFF",
      height: "31px",
      marginRight: theme.spacing(1),
      width: "31px"
    },
    starIcon: {
      color: "#BDBDBD",
      marginBottom: "15px",
      marginLeft: theme.spacing(1),
      marginTop: "32px"
    }
  }),
  {
    name: "couponRedemptionListPage"
  }
);

export interface CouponRedemptionListPageProps
  extends PageListProps,
    TabPageProps,
    FilterPageProps<
      CouponRedemptionFilterKeys,
      CouponRedemptionListUrlFilterOpts
    >,
    SortPage<CouponRedemptionListUrlSortField> {
  couponAvailability: number;
  couponChild: CouponRedemptions_couponRedemptions_edges_node[];
  couponRedemptions: CouponRedemptions_couponRedemptions_edges_node[];
  couponCount: number;
  params: CouponsUrlQueryParams;
  couponCode: string;
  couponStatus: string;
  isFeatured: boolean;
}

export const CouponRedemptionListPage: React.FC<CouponRedemptionListPageProps> = props => {
  const {
    couponAvailability,
    couponCode,
    couponCount,
    couponStatus,
    couponChild,
    isFeatured,
    params,
    currentTab,
    filterOpts,
    initialSearch,
    onSearchChange,
    onFilterChange,
    onTabChange,
    onTabDelete,
    onTabSave,
    currencySymbol,
    tabs,
    ...listProps
  } = props;
  const intl = useIntl();
  const navigate = useNavigator();
  const classes = useStyles(props);

  const [loading, setLoading] = useState(true);

  const structure = createFilterStructure(intl, filterOpts);

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);

  return (
    <Container>
      <div className={classes.buttonContainers}>
        <div className={classes.auxilliaryContainers}>
          <div
            className={classes.backBtn}
            id="couponRedemptionBackBtn"
            onClick={() => navigate(couponManageUrl)}
          >
            <ArrowBackIcon />
          </div>
          <label className={classes.label}>{couponCode}</label>
          <StarIcon
            className={isFeatured ? classes.featuredStarIcon : classes.starIcon}
          />
          <Chip
            className={classes.chips}
            label={couponStatus}
            style={{ backgroundColor: statusColors[couponStatus] }}
            icon={
              <FiberManualRecordIcon
                style={{ color: fiberIconColors[couponStatus] }}
              />
            }
          />
        </div>
      </div>
      {couponCount > couponAvailability ? (
        <Card>
          <>
            <FilterBar
              currencySymbol={currencySymbol}
              filterStructure={structure}
              searchPlaceholder=""
              initialSearch=""
              onFilterChange={onFilterChange}
              onSearchChange={onSearchChange}
            />
            <CouponRedemptionList {...listProps} />
          </>
        </Card>
      ) : loading ? (
        <></>
      ) : (
        <>
          <Card className={classes.card}>
            <div className={classes.imageContainer}>
              <img
                className={classes.couponIcon}
                src={couponRedemptionIcon}
                alt="emptyCouponRedemptionCode"
              />
            </div>
            <Typography className={classes.noCouponsText}>
              No coupons claimed yet!
            </Typography>
            <Typography className={classes.noCouponsDesc}>
              Coupons claimed will be displayed here.
            </Typography>
            <Typography className={classes.couponAvailable}>
              Coupons Remaining: {couponAvailability}
            </Typography>
            <Button
              className={classes.buttonNavigation}
              color="primary"
              onClick={() =>
                navigate(couponListUrl({ ...params, activeTab: "1" }))
              }
              variant="contained"
              id="navigateCouponList"
              style={{ backgroundColor: "#1848FE" }}
            >
              <span style={{ color: "#ffffff", fontWeight: "bold" }}>
                <FormattedMessage
                  defaultMessage="Go back to coupon list"
                  description="button"
                />
              </span>
            </Button>
          </Card>
        </>
      )}
    </Container>
  );
};

export default CouponRedemptionListPage;
